<template>
  <div class="calw">
    <div v-if="loading && !series" class="calw-skeleton"></div>
    <p v-else-if="!weeks.length" class="ov-empty">{{ $t('dash.overview.widgets.no_series') }}</p>
    <template v-else>
      <div class="calw-grid" :style="{ gridTemplateColumns: `28px repeat(${weeks.length}, minmax(10px, 1fr))` }">
        <template v-for="day in 7">
          <span :key="`l-${day}`" class="calw-day">{{ dayLabel(day - 1) }}</span>
          <span
            v-for="(week, w) in weeks"
            :key="`c-${day}-${w}`"
            class="calw-cell"
            :class="{ 'calw-cell--out': !week[day - 1] }"
            :style="{ background: week[day - 1] ? color(week[day - 1].value) : 'transparent' }"
            :title="week[day - 1] ? `${week[day - 1].label} : ${week[day - 1].value.toLocaleString()}` : ''"
          ></span>
        </template>
      </div>
      <div class="calw-legend">
        <span>{{ $t('dash.overview.best_times.less') }}</span>
        <span class="calw-scale"></span>
        <span>{{ $t('dash.overview.best_times.more') }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'

/**
 * Calendrier d'activité : une case par jour (impressions ou interactions), du clair au foncé
 */
export default {
  name: 'CalendarWidget',

  props: {
    options: { type: Object, default: () => ({}) },
    // séries de /reporting/overview : { impressions: [{date,total}], engagement: [...] }
    series: { type: Object, default: null },
    loading: { type: Boolean, default: false }
  },

  computed: {
    days() {
      const list = (this.series && this.series[this.options.metric || 'engagement']) || []
      const map = {}
      list.forEach(d => {
        const day = moment(d.date * 1000).format('YYYY-MM-DD')
        map[day] = (map[day] || 0) + (d.total || 0)
      })
      return map
    },

    max() {
      return Math.max(1, ...Object.values(this.days))
    },

    // Semaines (colonnes) du lundi au dimanche couvrant la période
    weeks() {
      const keys = Object.keys(this.days).sort()
      if (!keys.length) return []
      const start = moment(keys[0]).startOf('isoWeek')
      const end = moment(keys[keys.length - 1])
      const weeks = []
      for (const cursor = start.clone(); cursor.isSameOrBefore(end); cursor.add(1, 'week')) {
        weeks.push(Array.from({ length: 7 }, (_, i) => {
          const day = cursor.clone().add(i, 'days')
          const key = day.format('YYYY-MM-DD')
          if (day.isAfter(end) || key < keys[0]) return null
          return { label: day.format('dddd DD/MM/YYYY'), value: this.days[key] || 0 }
        }))
      }
      return weeks
    }
  },

  methods: {
    dayLabel(day) {
      return moment().locale((this.$i18n && this.$i18n.locale) || 'fr').isoWeekday(day + 1).format('dd')
    },

    // 0 = blanc, puis du clair au foncé (couleur Mashup Web Social)
    color(value) {
      if (!value) return '#ffffff'
      const t = Math.pow(value / this.max, 0.6)
      const light = [241, 237, 245]
      const plum = [107, 31, 77]
      return `rgb(${light.map((v, i) => Math.round(v + (plum[i] - v) * t)).join(', ')})`
    }
  }
}
</script>

<style scoped>
.calw {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.calw-skeleton {
  flex: 1;
  border-radius: 6px;
  background: #f1edf5;
}
.calw-grid {
  display: grid;
  gap: 3px;
  align-items: center;
  overflow-x: auto;
}
.calw-day {
  font-size: 10.5px;
  color: #9ba3b7;
  text-transform: capitalize;
}
.calw-cell {
  aspect-ratio: 1;
  max-height: 22px;
  border-radius: 3px;
  border: 1px solid #efe7f3;
}
.calw-cell--out {
  border-color: transparent;
}
.calw-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 10px;
  font-size: 11px;
  color: #9ba3b7;
}
.calw-scale {
  width: 90px;
  height: 8px;
  border-radius: 4px;
  border: 1px solid #efe7f3;
  background: linear-gradient(90deg, rgb(241, 237, 245), rgb(107, 31, 77));
}
</style>
